<template lang="pug">
#send-safety-confirm-to-other-user-detail
  // title
  .d-flex.flex-row.justify-content-center.mb-1
    .page-title 施設関係者の安否確認結果
  .mb-3.separator

  // menu list
  b-card.mb-4(no-body)
    // message detail
    .message-accordion
      MessageAccordion(v-model="messageAccordionModel")

    // response rate
    .response-rate-accordion
      ResponseRateAccordion

    // reply otheruser
    .reply-other-users-accordion
      ReplyListAccordion(:type="1")

    // nonreply otheruser
    .nonreply-other-users-accordion
    .reply-other-users-accordion
      ReplyListAccordion(:type="0")

    // answers
    .answers(v-for="(confirm, index) in displayConfirms")
      b-card-header.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle="'accordion-' + confirm.name")
        span.toggle-text {{ confirm.label }}
      b-collapse(:id="'accordion-' + confirm.name" :accordion="'accordion-' + confirm.name" role="tabpanel" @show="showAccordion(index)")
        b-card-body
          .answer-graph.mb-3(:id="confirm.name")
          .table-options.d-flex.flex-row.justify-content-end.align-items-center
            .mb-2.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap(v-if="!isBusy")
              span.table-option-text 施設関係者グループ:
              .filter.mr-0
                b-form-select(:id="'filter-input-' + index" v-model="confirm.filter.groupId" :options="confirm.filter.filterOptions"
                  size="md" @change="filterGraphItem(index)")

  // controll buttons
  .mb-0
    span.key-name 安否確認の操作
  #controll-buttons.mb-4
    .d-flex.flex-row.flex-wrap.mb-1
      b-button.btn.download-btn.mr-2(@click="onCsvDownload") 回答結果のダウンロード
      b-button.btn.change-status-btn.mr-2(@click="onChangeStatus") 受付状態の変更
      b-button.btn.delete-btn(@click="onDelete") 削除

  // buttons
  #buttons.mb-0
    .d-flex.flex-row.justify-content-center
      b-button.btn.back-btn(@click="onBack") 戻る

  // loading modal
  Loading(:isBusy="isBusy" :message="loadingMessage")
  // change status modal
  OkCancelModal(v-model="showChangeStatusModal" :title="changeStatusModalTitle" @ok="changeStatus")
  OkModal(v-model="showChangeStatusSuccessModal" title="受付状態の変更に成功しました。" @ok="createView")
  // delete modal
  OkCancelModal(v-model="showDeleteModal" title="施設関係者の安否確認情報を削除しますか？" @ok="deleteOtherUserSafetyConfirm")
  OkModal(v-model="showDeleteSuccessModal" title="安否確認情報の削除に成功しました。" @ok="onBack")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileSendSafetyConfirmDetailToOtherUserState } from "@/store/MobileSendSafetyConfirmDetailToOtherUser";
import { ChartData } from "chart.js";
import { MobileUrl } from "@/commons/constants/MobileUrl";
import SystemError from "@/commons/models/SystemError";

import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserSafetyConfirmMastersService, {
  OtherUserSafetyConfirmMasterInputType,
  OtherUserSafetyConfirmMasterRadioInputOption,
  OtherUserSafetyConfirmMastersGetModel
} from "@/commons/services/OtherUserSafetyConfirmMasters";
import SafetyConfirmsService, { SafetyConfirmsGetModel, OtherUserSafetyConfirmsPatchModel } from "@/commons/services/SafetyConfirms";
import SendSafetyConfirmMessagesService, { SendOtherUserSafetyConfirmLineMessagesGetModel } from "@/commons/services/SendSafetyConfirmMessages";
import SafetyConfirmReplyMessagesService, { SafetyConfirmReplyMessagesGetModel } from "@/commons/services/SafetyConfirmReplyMessages";

import Loading from "@/mobile/basics/Loading.vue";
import AccordionLoading from "@/mobile/basics/AccordionLoading.vue";
import MessageAccordion, { MessageAccordionModel } from "@/mobile/containers/SendSafetyConfirmToOtherUserDetail/MessageAccordion.vue";
import ResponseRateAccordion from "@/mobile/containers/SendSafetyConfirmToOtherUserDetail/ResponseRateAccordion.vue";
import ReplyListAccordion from "@/mobile/containers/SendSafetyConfirmToOtherUserDetail/ReplyListAccordion.vue";
import DoughnutChart from "@/mobile/basics/SafetyConfirmDoughnutChart.vue";
import OkCancelModal from "@/mobile/basics/OkCancelModal.vue";
import OkModal from "@/mobile/basics/OkModal.vue";

/**
 * ### 安否確認入力依頼のメッセージ送信(施設関係者) 詳細コンポーネント
 */
@Component({
  components: {
    Loading,
    AccordionLoading,
    MessageAccordion,
    ResponseRateAccordion,
    ReplyListAccordion,
    DoughnutChart,
    OkCancelModal,
    OkModal
  }
})
export default class SendSafetyConfirmToOtherUserDetail extends Vue {
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 安否確認(施設関係者)で送信する質問内容のマスタサービス */
  @Inject("OtherUserSafetyConfirmMastersService") otherUserSafetyConfirmMastersService!: OtherUserSafetyConfirmMastersService;
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;
  /** 安否確認(施設関係者)メッセージ送信サービス */
  @Inject("SendSafetyConfirmMessagesService") sendSafetyConfirmMessagesService!: SendSafetyConfirmMessagesService;
  /** 安否確認回答サービス */
  @Inject("SafetyConfirmReplyMessagesService") safetyConfirmReplyMessagesService!: SafetyConfirmReplyMessagesService;

  /** ### 安否確認詳細画面で必要な情報 */
  @Getter("mobileSendSafetyConfirmDetailToOtherUserState") state!: MobileSendSafetyConfirmDetailToOtherUserState;

  /** グラフの色 */
  readonly graphColors: string[] = [
    "rgba(131, 205, 208, 1)",
    "rgba(230, 220, 105, 1)",
    "rgba(147, 186, 250, 1)",
    "rgba(161, 206, 151, 1)",
    "rgba(249, 204, 216, 1)",
    "rgba(253, 188, 103, 1)",
    "rgba(199, 182, 216, 1)",
    "rgba(219, 173, 137, 1)",
    "rgba(185, 185, 185, 1)"
  ];

  /** 読み込み中モーダル表示可否 */
  isBusy: boolean = false;
  /** 読み込み中モーダルメッセージ */
  loadingMessage: string = "読込み中";

  /** 状態変更確認モーダルタイトル */
  changeStatusModalTitle: string = "";
  /** 状態変更確認モーダル表示可否 */
  showChangeStatusModal: boolean = false;
  /** 状態変更完了モーダル表示可否 */
  showChangeStatusSuccessModal: boolean = false;

  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** メッセージ内容アコーディオン表示内容 */
  messageAccordionModel: MessageAccordionModel = {
    acceptingOtherUserReply: "",
    groupName: "",
    openingSentence: ""
  };

  /** 施設関係者への安否確認質問のMAP */
  mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel> = new Map<number, OtherUserSafetyConfirmMastersGetModel>();
  /** 安否確認(施設関係者)の回答 */
  replyMessages: Array<SafetyConfirmReplyMessagesGetModel> = [];
  /** 安否確認グラフの最大文字数 */
  maxLabelLength: number = 0;
  /** 回答者施設関係者モーダルフィルタリング */
  otherUserGroupFilterOptions: Array<{ value: string; text: string }> = [];
  /** 安否確認の回答リスト */
  displayConfirms: Array<Confirm> = [];
  /** グラフインスタンス */
  graphInstances: Array<DoughnutChart> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    const otherUserGroups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    const otherUserGroupFilterOptions: Array<{ value: string; text: string }> = await this.createOtherUserGroupFilterOptions(otherUserGroups);
    this.otherUserGroupFilterOptions = otherUserGroupFilterOptions;

    let messages: Array<SendOtherUserSafetyConfirmLineMessagesGetModel> = [];
    for (const sortKey of this.state.otherUserMessagesSortKey as string[]) {
      const message: SendOtherUserSafetyConfirmLineMessagesGetModel = await this.getMessage(sortKey);
      messages = messages.concat(message);
    }

    let replyMessages: Array<SafetyConfirmReplyMessagesGetModel> = [];
    for (const sortKey of this.state.otherUserMessagesSortKey as string[]) {
      replyMessages = replyMessages.concat(await this.getSafetyConfirmReplyMessages(sortKey));
    }
    this.replyMessages = replyMessages;

    const receivers: string[] = [];
    for (const message of messages) {
      for (const receiver of message.receiverDetails) {
        receivers.push(receiver.groupName);
      }
    }
    this.messageAccordionModel = {
      acceptingOtherUserReply: this.state.acceptingOtherUserReply == true ? "受付中" : "受付停止",
      groupName: [...new Set(receivers)].join("、"),
      openingSentence: messages[0].functionContents.openingSentence
    };

    const masters: Array<OtherUserSafetyConfirmMastersGetModel> = await this.getOtherUserSafetyConfirmMasters();
    const mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel> = this.createMasterMap(masters);
    this.mastersMap = mastersMap;

    const maxLabelLength: number = this.checkMaxLabelLength(messages[0].functionContents.confirmationContents, mastersMap);

    const displayConfirms: Array<Confirm> = this.createDisplayConfirms(messages[0].functionContents.confirmationContents, mastersMap, replyMessages, maxLabelLength);
    this.displayConfirms = displayConfirms;

    // DoughnutChartはmounted前に子コンポーネントが存在していない場合は描画されない
    // しかし、質問数は不明のためDoughnutChartを何個表示するかが分からないため、情報取得してドキュメントの描画が終わったら動的にDoughnutChartを追加する
    this.$nextTick(() => {
      // 既存DoughnutChartインスタンスを削除する
      for (const instance of this.graphInstances) {
        instance.$destroy();
      }
      // インスタンスを削除したのでリストを初期化する
      this.graphInstances = [];

      for (const displayConfirm of displayConfirms) {
        const graphInstance: any = this.addGraph(displayConfirm);
        this.graphInstances.push(graphInstance);
      }
    });

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 施設関係者グループフィルター用選択項目
   *
   * - 検索用に施設関係者グループマスターのMAPを作る
   * - アカウント情報から許可されている施設関係者グループIDの配列を取得する
   * - 施設関係者グループマスターと照合して選択項目の施設関係者グループを作成する
   */
  public createOtherUserGroupFilterOptions(otherUserGroups: Array<OtherUserGroupModel>): Array<{ value: string; text: string }> {
    let filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    let groupMap: Map<number, OtherUserGroupModel> = new Map<number, OtherUserGroupModel>();
    for (const group of otherUserGroups) {
      groupMap.set(group.id, group);
    }

    const allowedGroups: number[] = userStore.loginUser.permissions.otherUserGroups;
    for (const allowedGroup of allowedGroups) {
      const group: OtherUserGroupModel | undefined = groupMap.get(allowedGroup);
      if (group) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 安否確認(施設関係者)で送信する質問内容のマスタを取得する
   */
  public async getOtherUserSafetyConfirmMasters(): Promise<Array<OtherUserSafetyConfirmMastersGetModel>> {
    const response: Array<OtherUserSafetyConfirmMastersGetModel> = await this.otherUserSafetyConfirmMastersService.get(userStore);
    return response;
  }

  /**
   * ### 質問内容マスタのMAPを作成する
   */
  public createMasterMap(masters: Array<OtherUserSafetyConfirmMastersGetModel>): Map<number, OtherUserSafetyConfirmMastersGetModel> {
    let map: Map<number, OtherUserSafetyConfirmMastersGetModel> = new Map<number, OtherUserSafetyConfirmMastersGetModel>();

    for (const master of masters) {
      map.set(master.id, master);
    }

    return map;
  }

  /**
   * ### 安否確認を取得する
   */
  public async getSafetyConfirm(safetyConfirmsId: number): Promise<SafetyConfirmsGetModel> {
    const response: SafetyConfirmsGetModel = await this.safetyConfirmsService.get(userStore, safetyConfirmsId);
    return response;
  }

  /**
   * ### 安否確認(施設関係者)メッセージを取得する
   */
  public async getMessage(parentOtherUserMessagesSortKey: string): Promise<SendOtherUserSafetyConfirmLineMessagesGetModel> {
    const response: SendOtherUserSafetyConfirmLineMessagesGetModel = await this.sendSafetyConfirmMessagesService.getOtherUserMessage(userStore, parentOtherUserMessagesSortKey);
    return response;
  }

  /**
   * ### 安否確認(施設関係者)の回答を取得する
   */
  public async getSafetyConfirmReplyMessages(messagesSortKey: string): Promise<Array<SafetyConfirmReplyMessagesGetModel>> {
    const response: Array<SafetyConfirmReplyMessagesGetModel> = await this.safetyConfirmReplyMessagesService.gets(userStore, messagesSortKey);
    return response;
  }

  /**
   * ### 画面表示用の質問項目を作成する
   *
   * - 安否確認メッセージで質問したものだけを返す
   * - 回答内容をグラフ表示するための情報を作成する
   *   - グラフの都合上ラベルの文字数を統一する必要があるので統一する
   *   - グラフ表示するか否かは質問の入力種別で判断する(現在はラジオボタンのみ)
   *   - グラフでは各施設関係者からの回答を集計した情報を表示する
   */
  public createDisplayConfirms(
    confirms: number[],
    mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel>,
    replyMessages: Array<SafetyConfirmReplyMessagesGetModel>,
    maxLabelLength: number
  ): Array<Confirm> {
    let displayConfirms: Array<Confirm> = [];

    for (const confirm of confirms) {
      let displayConfirm: Confirm | undefined = mastersMap.get(confirm);
      if (displayConfirm != undefined) {
        switch (displayConfirm.inputType) {
          case OtherUserSafetyConfirmMasterInputType.RADIO: {
            const answerCountMap: Map<string, number> = this.createAnswerCountMap(replyMessages, displayConfirm.id);
            const chartjsData: ChartData = this.createRadioDoughnutChartData(displayConfirm, maxLabelLength, answerCountMap);
            displayConfirm.rateGraph = chartjsData;
            break;
          }
          case OtherUserSafetyConfirmMasterInputType.MULTIPLE_TEXT: {
            const chartjsData: ChartData = this.createMultipleTextDoughnutChartData(displayConfirm, maxLabelLength, replyMessages, displayConfirm.id);
            displayConfirm.rateGraph = chartjsData;
            break;
          }
          default: {
            break;
          }
        }

        displayConfirm.displayRateGraph = Object.assign({}, displayConfirm.rateGraph);

        displayConfirm.filter = {
          groupId: "",
          filterOptions: this.otherUserGroupFilterOptions.concat()
        };

        displayConfirms.push(displayConfirm);
      }
    }

    return displayConfirms;
  }

  /**
   * ### 画面で使用する質問事項の中で最大文字数のラベルの文字数を調べる
   */
  public checkMaxLabelLength(confirms: number[], mastersMap: Map<number, OtherUserSafetyConfirmMastersGetModel>): number {
    let maxLabelLength: number = 0;
    for (const confirm of confirms) {
      let displayConfirm: Confirm | undefined = mastersMap.get(confirm);
      if (displayConfirm != undefined) {
        switch (displayConfirm.inputType) {
          case OtherUserSafetyConfirmMasterInputType.RADIO: {
            const inputOptions: OtherUserSafetyConfirmMasterRadioInputOption = displayConfirm.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
            for (const choice of inputOptions.choices) {
              if (maxLabelLength < choice.label.length) {
                maxLabelLength = choice.label.length;
              }
            }
          }
        }
      }
    }
    return maxLabelLength;
  }

  /**
   * ### 質問IDに対して、質問の回答の集計したMAPを作成する
   *
   * - 最新の情報でなければ集計対象ではない
   */
  public createAnswerCountMap(replyMessages: Array<SafetyConfirmReplyMessagesGetModel>, answerId: number): Map<string, number> {
    let answerCountMap: Map<string, number> = new Map<string, number>();
    for (const replyMessage of replyMessages) {
      if (replyMessage.latest == false) {
        continue;
      }
      for (const answer of replyMessage.answers) {
        if (answer.id == answerId) {
          let answerCount: number | undefined = answerCountMap.get(answer.answer);
          if (answerCount != undefined) {
            answerCount++;
            answerCountMap.set(answer.answer, answerCount);
          } else {
            answerCountMap.set(answer.answer, 1);
          }
        }
      }
    }
    return answerCountMap;
  }

  /**
   * ### DoughnutChart用のグラフ情報を作成する
   */
  public createRadioDoughnutChartData(displayConfirm: Confirm, maxLabelLength: number, answerCountMap: Map<string, number>): ChartData {
    let counts: number[] = [];
    let labels: string[] = [];
    let backgroundColors: string[] = [];
    let borderWidths: Array<number> = [];
    const inputOptions: OtherUserSafetyConfirmMasterRadioInputOption = displayConfirm.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
    for (const choice of inputOptions.choices) {
      labels.push(this.paddingGraphLabel(choice.label, maxLabelLength));
      let count: number | undefined = answerCountMap.get(choice.value);
      if (count != undefined) {
        counts.push(count);
      } else {
        counts.push(0);
      }
      backgroundColors.push(this.graphColors[Number(choice.value)]);
      borderWidths.push(0);
    }
    const chartjsData: ChartData = {
      labels: labels,
      datasets: [
        {
          label: displayConfirm.label,
          backgroundColor: backgroundColors,
          borderWidth: borderWidths,
          data: counts
        }
      ]
    };

    return chartjsData;
  }

  /**
   * ### 文字数を引数の最大文字数に統一する
   *
   * - 置き換え文字は「・」
   */
  public paddingGraphLabel(label: string, maxLenght: number): string {
    let paddingText: string = "";
    for (let i = label.length; i < maxLenght; i++) {
      paddingText += "・";
    }
    return `${label}${paddingText}`;
  }

  /**
   * ### DoughnutChart用のグラフ情報を作成する
   */
  public createMultipleTextDoughnutChartData(
    displayConfirm: Confirm,
    maxLabelLength: number,
    replyMessages: Array<SafetyConfirmReplyMessagesGetModel>,
    answerId: number
  ): ChartData {
    let inputedCount: number = 0;
    let noninputedCount: number = 0;
    for (const replyMessage of replyMessages) {
      if (replyMessage.latest == false) {
        continue;
      }
      for (const answer of replyMessage.answers) {
        if (answer.id == answerId) {
          if (answer.answer) {
            inputedCount++;
          } else {
            noninputedCount++;
          }
        }
      }
    }
    const chartjsData: ChartData = {
      labels: [this.paddingGraphLabel("入力", maxLabelLength), this.paddingGraphLabel("未入力", maxLabelLength)],
      datasets: [
        {
          label: displayConfirm.label,
          backgroundColor: [this.graphColors[0], this.graphColors[1]],
          borderWidth: [0, 0],
          data: [inputedCount, noninputedCount]
        }
      ]
    };
    return chartjsData;
  }

  /**
   * ### グラフを画面に追加する
   *
   * - DoughnutChartは画面作成時にコンポーネントが存在しないと描画が正しく行われない
   * - 画面のDOM要素にDoughnutChartのVueインスタンスを動的に作成して追加する
   * - ただPropを指定しても描画されないのでchartDataChanged()を呼び出して無理やり描画させている
   * - あとで削除できるようにインスタンスは保持しておく
   * - 実行時に既に登録されているインスタンスは削除する
   *   - あわせて作られているinnerHtmlも削除する
   */
  public addGraph(displayConfirm: Confirm): any {
    // DoughnutChartを追加するDOMを取得
    const element: HTMLElement | null = document.getElementById(displayConfirm.name);
    if (element != null) {
      // 既に子要素がある場合があるので削除する
      element.innerHTML = "";
      // DoughnutChartのインスタンスを作成
      const doughnutChart = Vue.extend(DoughnutChart);
      const instance: any = new doughnutChart({
        propsData: {
          chartData: displayConfirm.displayRateGraph,
          height: 240,
          width: 640
        }
      });
      // インスタンスをマウント
      instance.$mount();
      // 無理やり描画を実施
      instance.chartDataChanged();
      // 描画したDoughnutChartをDOMに追加する
      element.appendChild(instance.$el);

      return instance;
    }

    return null;
  }

  /**
   * ### CSVエクスポート
   */
  public async onCsvDownload(): Promise<void> {
    let replyMessages: Array<SafetyConfirmReplyMessagesGetModel> = [];
    for (const sortKey of this.state.otherUserMessagesSortKey as string[]) {
      const messages: Array<SafetyConfirmReplyMessagesGetModel> = await this.getSafetyConfirmReplyMessages(sortKey);
      replyMessages = replyMessages.concat(messages);
    }

    const csv: string = this.createCsv(replyMessages, this.displayConfirms, this.mastersMap);
    const fileName: string = this.createCsvFileName(this.state as SafetyConfirmsGetModel);
    this.downloadCsv(csv, fileName);

    return;
  }

  /**
   * ### 安否確認の返信のCSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   *   - 入力種別がラジオボタンの場合はラジオボタンのラベル名を使用する
   *   - 入力種別がテキスト入力(複数行)の場合は入力内容をそのまま使用する
   */
  public createCsv(
    replyMessages: Array<SafetyConfirmReplyMessagesGetModel>,
    displayConfirms: Array<Confirm>,
    masterMap: Map<number, OtherUserSafetyConfirmMastersGetModel>
  ): string {
    let rowNames: string[] = [];
    for (const displayConfirm of displayConfirms) {
      rowNames.push(displayConfirm.label);
    }

    let csv: string = `\ufeff施設関係者グループ名,施設関係者ユーザー名,所属,電話番号,登録日時,最新の登録,${rowNames.join(",")}\n`;

    for (const replyMessage of replyMessages) {
      let columns: string[] = [];

      for (const answer of replyMessage.answers) {
        let answerText: string = "";
        const master: OtherUserSafetyConfirmMastersGetModel = masterMap.get(answer.id) as OtherUserSafetyConfirmMastersGetModel;
        switch (master.inputType) {
          case OtherUserSafetyConfirmMasterInputType.RADIO: {
            const radioInputOption: OtherUserSafetyConfirmMasterRadioInputOption = master.inputOptions as OtherUserSafetyConfirmMasterRadioInputOption;
            for (const choice of radioInputOption.choices) {
              if (answer.answer == choice.value) {
                answerText = choice.label;
              }
            }
            break;
          }
          case OtherUserSafetyConfirmMasterInputType.MULTIPLE_TEXT: {
            if (answer.answer != null) {
              answerText = answer.answer;
            }
            break;
          }
          default: {
            break;
          }
        }
        columns.push(answerText);
      }

      const latestText: string = replyMessage.latest == true ? "はい" : "いいえ";
      const date: string = this.$dateUtil.convertNumberToString(replyMessage.created, "yyyy/mm/dd hh:MM:ss");
      csv += `${replyMessage.groupName},${replyMessage.groupMemberName},${replyMessage.groupMemberOrganization},="${
        replyMessage.groupMemberTel
      }",${date},${latestText},${columns.join(",")}\n`;
    }

    return csv;
  }

  /**
   * ### CSVファイル名を作成する
   */
  public createCsvFileName(safetyConfirm: SafetyConfirmsGetModel): string {
    const date: string = this.$dateUtil.convertNumberToString(safetyConfirm.created, "yyyymmdd");
    const fileName: string = `HitomeQコネクト-安否確認-施設関係者-${safetyConfirm.name}-${date}.csv`;

    return fileName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  public downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    return;
  }

  /**
   * ### 状態変更確認モーダルを表示する
   */
  public onChangeStatus(): void {
    if (this.state.acceptingOtherUserReply == true) {
      this.changeStatusModalTitle = "受付状態を受付停止に変更しますか？";
    } else {
      this.changeStatusModalTitle = "受付状態を受付中に変更しますか？";
    }
    this.showChangeStatusModal = true;

    return;
  }

  /**
   * ### 安否確認の受付状態を更新する
   */
  public async changeStatus(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中";

    try {
      const safetyConfirm: SafetyConfirmsGetModel = this.state as SafetyConfirmsGetModel;
      const requestBody: OtherUserSafetyConfirmsPatchModel = this.createChangeStatusRequestBody(safetyConfirm);
      await this.safetyConfirmsService.patchOtherUser(userStore, safetyConfirm.id, requestBody);
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }
    this.showChangeStatusSuccessModal = true;

    return;
  }

  /**
   * ### 安否確認の受付状態更新のリクエストボディを作成する
   */
  public createChangeStatusRequestBody(safetyConfirm: SafetyConfirmsGetModel): OtherUserSafetyConfirmsPatchModel {
    const requestBody: OtherUserSafetyConfirmsPatchModel = {
      otherUserMessagesSortKey: safetyConfirm.otherUserMessagesSortKey ? safetyConfirm.otherUserMessagesSortKey : null,
      sendedOtherUserLine: safetyConfirm.sendedOtherUserLine,
      sendedOtherUserMail: safetyConfirm.sendedOtherUserMail,
      acceptingOtherUserReply: !safetyConfirm.acceptingOtherUserReply,
      updated: safetyConfirm.updated
    };

    return requestBody;
  }

  /**
   * ### 削除確認モーダルを表示する
   */
  public onDelete(): void {
    this.showDeleteModal = true;
    return;
  }

  /**
   * ### 安否確認の施設関係者情報を削除する
   */
  public async deleteOtherUserSafetyConfirm(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中";

    try {
      const safetyConfirm: SafetyConfirmsGetModel = this.state as SafetyConfirmsGetModel;
      const requestBody: OtherUserSafetyConfirmsPatchModel = this.createDeleteRequestBody(safetyConfirm);
      await this.safetyConfirmsService.patchOtherUser(userStore, safetyConfirm.id, requestBody);

      this.showDeleteSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 安否確認の施設関係者情報の削除のリクエストボディを作成する
   */
  public createDeleteRequestBody(safetyConfirm: SafetyConfirmsGetModel): OtherUserSafetyConfirmsPatchModel {
    const requestBody: OtherUserSafetyConfirmsPatchModel = {
      otherUserMessagesSortKey: null,
      sendedOtherUserLine: false,
      sendedOtherUserMail: false,
      acceptingOtherUserReply: false,
      updated: safetyConfirm.updated
    };

    return requestBody;
  }

  /**
   * ### グラフの内容をフィルタリングしたもので再描画する
   */
  public filterGraphItem(index: number): void {
    const filter: ConfirmFilter = this.displayConfirms[index].filter as ConfirmFilter;
    const groupId: string = filter.groupId as string;

    if (groupId == "") {
      this.displayConfirms[index].displayRateGraph = Object.assign({}, this.displayConfirms[index].rateGraph);
    } else {
      let filterdReplyMessages: Array<any> = [];
      for (const replyMessage of this.replyMessages) {
        if (groupId != "" && replyMessage.groupId != Number(groupId)) {
          continue;
        }
        filterdReplyMessages.push(replyMessage);
      }

      switch (this.displayConfirms[index].inputType) {
        case OtherUserSafetyConfirmMasterInputType.RADIO: {
          const answerCountMap: Map<string, number> = this.createAnswerCountMap(filterdReplyMessages, this.displayConfirms[index].id);
          const chartjsData: ChartData = this.createRadioDoughnutChartData(this.displayConfirms[index], this.maxLabelLength, answerCountMap);
          this.displayConfirms[index].displayRateGraph = chartjsData;
          break;
        }
        case OtherUserSafetyConfirmMasterInputType.MULTIPLE_TEXT: {
          const chartjsData: ChartData = this.createMultipleTextDoughnutChartData(
            this.displayConfirms[index],
            this.maxLabelLength,
            filterdReplyMessages,
            this.displayConfirms[index].id
          );
          this.displayConfirms[index].displayRateGraph = chartjsData;
          break;
        }
        default: {
          break;
        }
      }
    }

    // DoughnutChartはmounted前に子コンポーネントが存在していない場合は描画されない
    // しかし、質問数は不明のためDoughnutChartを何個表示するかが分からないため、情報取得してドキュメントの描画が終わったら動的にDoughnutChartを追加する
    this.$nextTick(() => {
      // 既存DoughnutChartインスタンスを削除する
      this.graphInstances[index].$destroy();
      // あとで削除できるようにインスタンスは保持しておく
      const instance: any = this.addGraph(this.displayConfirms[index]);
      this.graphInstances[index] = instance;
    });

    return;
  }

  /**
   * ### アコーディオンのオープンイベント
   *
   * - アコーディオンがクローズするとDoughnutChartのheightが0になってしまい、オープン⇒クローズ⇒オープンでグラフが表示されなくなる
   * - アコーディオンがオープンされたタイミングで再描画を実施する
   */
  public showAccordion(index: number): void {
    const graphInstance: DoughnutChart = this.graphInstances[index];
    // graphInstanceがnullの場合がありえるようなのでチェックしてから実施する
    if (graphInstance) {
      graphInstance.chartDataChanged();
    }
    return;
  }

  /**
   * ### 戻るボタン押下イベント
   *
   * - 安否確認一覧画面へ遷移する
   */
  public onBack(): void {
    this.$router.push(MobileUrl.SafetyConfirmList);
    return;
  }
}

interface Confirm extends OtherUserSafetyConfirmMastersGetModel {
  /** 回答グラフ作成で使用するグラフデータ */
  rateGraph?: ChartData;
  /** 表示するグラフデータ */
  displayRateGraph?: ChartData;
  /** フィルター用情報 */
  filter?: ConfirmFilter;
}

interface ConfirmFilter {
  /** フィルタリング対象 */
  groupId?: string;
  /** フィルタリングオプション */
  filterOptions?: Array<{ value: string; text: string }>;
}
</script>

<style lang="scss" scopeed>
@import "../../../styles/mobile/_colors.scss";

#send-safety-confirm-to-other-user-detail {
  .delete-btn,
  .back-btn {
    min-width: 84px;
  }
}
</style>
